import { BrandMark, StatusBadge } from '@argink/ui';
import { ENVELOPE_STATUS_LABEL } from '@argink/shared/surface-lexicon';
import { OVERVIEW_ATTENTION_TITLE, OVERVIEW_METRIC_LABEL, OVERVIEW_TITLE } from '../../lib/copy';

/**
 * A superfície do produto que domina a primeira dobra da Home.
 *
 * Densidade é o ponto: um print esparso lê como maquete. Aqui há navegação,
 * busca, indicadores, tabela com progresso e a fila de pendências — que é o que
 * uma tela de trabalho real tem.
 *
 * **Isto é a foto da tela que existe, e a M4 mudou o que existe** (§32). Antes desta
 * rodada a janela mostrava uma navegação de sete itens — "Documentos",
 * "Participantes", "Convites", "Relatórios" — dos quais cinco nunca existiram, e uma
 * coluna de "Atividade recente" nominal ("Marina Alves assinou…") que exigiria um
 * stream de eventos que o BFF do dono **não tem**. Uma landing que mostra recurso
 * inexistente é a mesma fake product surface que o §11 proíbe na aplicação; o fato de
 * ser marketing não muda o que ela promete.
 *
 * Agora a navegação e os rótulos saem das MESMAS constantes que a Visão geral
 * consome (`lib/copy.ts`), então divergir volta a ser trabalho: mudar o produto sem
 * mudar a Home passa a quebrar aqui.
 */

const METRICS = [
  { value: '12', label: OVERVIEW_METRIC_LABEL.waiting },
  { value: '23', label: OVERVIEW_METRIC_LABEL.completed },
  { value: '3', label: OVERVIEW_METRIC_LABEL.attention },
];

const DOCUMENTS = [
  { name: 'Contrato de prestação de serviços', people: 4, signed: 3, total: 4, when: 'Hoje, 09:41' },
  { name: 'Aditivo contratual, Fornecedor Norte', people: 3, signed: 2, total: 3, when: 'Hoje, 08:15' },
  { name: 'Proposta comercial', people: 2, signed: 2, total: 2, when: 'Ontem, 16:32' },
  { name: 'Política de privacidade', people: 1, signed: 1, total: 1, when: 'Ontem, 11:07' },
];

/**
 * A fila de pendências, no lugar do antigo feed nominal.
 *
 * As três frases são **literalmente** as que `lib/dashboard.ts` produz — recusa,
 * prazo e envio parado, os três casos que a lista paginada do BFF de fato sustenta.
 * Nenhuma delas nomeia quem fez o quê, porque a resposta que alimenta a tela real
 * não traz participante nenhum (D-080: canal identifica canal, não estado).
 */
const ATTENTION = [
  { name: 'Proposta comercial', what: 'Um participante recusou assinar.', tone: 'danger' as const },
  { name: 'Distrato com anuência', what: 'O prazo termina em menos de 48 horas.', tone: 'warning' as const },
  { name: 'Ata de assembleia', what: 'Pronto para envio — nenhum convite saiu ainda.', tone: 'info' as const },
];

/**
 * A declaração de que a janela é uma ILUSTRAÇÃO vive numa legenda editorial, fora do
 * quadro.
 *
 * Ela já esteve dentro, no topo da janela, ocupando o lugar onde antes havia um campo
 * de busca — e com borda tracejada e largura de campo ela **lia como um input vazio**:
 * um rótulo que se disfarça de controle é ambiguidade nova no lugar da que se queria
 * remover. Como legenda de figura ela é inequívoca, não disputa espaço com a
 * composição e continua ligada ao quadro por semântica (`figure`/`figcaption`), não
 * por proximidade.
 *
 * O `aria-label` **começa** pelo texto da legenda e continua com a ressalva por
 * extenso: quem olha lê duas palavras, quem navega por regiões ouve a frase inteira, e
 * o rótulo falado contém o escrito (a regra de "label in name"). Fica explícito em vez
 * de depender do `figcaption` nomear o `figure`, que nem toda pilha de acessibilidade
 * computa igual.
 */
const ILLUSTRATION_LABEL =
  'Dados ilustrativos — representação da tela Visão geral do ARGINK; os números não são de nenhum cliente e não representam dados reais.';

/**
 * Rótulos da navegação ILUSTRADA nesta janela — fixture PRÓPRIA, desacoplada da
 * navegação real do produto (`PRODUCT_NAV`, `lib/copy.ts`).
 *
 * R-UX02-P01/D-101 productizou a nav real para dois itens (Painel, Envios) —
 * "Novo envio" virou CTA de topbar e "Configurações" saiu da nav (D-100). Esta
 * ilustração continua mostrando os MESMOS quatro rótulos que sempre mostrou,
 * porque ela é uma FOTO de layout com a ressalva "Dados ilustrativos" já
 * explícita no `figcaption`/`aria-label` acima — não uma promessa de superfície
 * viva. Redesenhar esta janela é decisão de Home pública, fora desta lane
 * (`docs/design/home/**` está fora do ownership do contrato L3A).
 */
const ILLUSTRATION_NAV_LABELS = ['Visão geral', 'Envios', 'Novo envio', 'Configurações'] as const;

export function DashboardPreview() {
  return (
    <figure className="app-illustration" aria-label={ILLUSTRATION_LABEL}>
      <div className="app-window">
      {/* `div`, não `aside`/`nav`: isto é a FOTO de um aplicativo dentro da página,
        * não uma região de navegação do documento. Como landmark, os dois painéis
        * desta janela viravam marcos anônimos e duplicados na árvore de
        * acessibilidade (`landmark-unique`, pego pelo axe). */}
      <div className="app-window__sidebar">
        {/* Componente de marca, nunca a palavra digitada. O lockup narrativo exige
          * ≥320px (brandbook §8) e não cabe nesta barra, então aqui entra a variante
          * `wordmark`, que é a representação oficial do design system para espaço
          * pequeno. */}
        <span className="app-window__brand">
          <BrandMark variant="wordmark" width={84} />
        </span>
        <div className="app-window__nav">
          {ILLUSTRATION_NAV_LABELS.map((label, index) => (
            <span key={label} className={index === 0 ? 'is-active' : undefined}>
              {label}
            </span>
          ))}
        </div>
      </div>

      <div className="app-window__main">
        <div className="app-window__topbar">
          {/* Aqui havia um campo de busca, que a Visão geral real **não tem** — a busca
            * mora na lista de envios. A foto mostrava um controle que a tela fotografada
            * não possui, então o espaço ficou vazio de propósito: a declaração de que
            * isto é ilustração está na legenda, fora do quadro. */}
          <span className="app-window__topbar-spacer" aria-hidden="true" />
          <span className="app-window__avatar">FS</span>
        </div>

        <div className="app-window__body">
          <div className="app-window__content">
            <p className="app-window__title">{OVERVIEW_TITLE}</p>

            <div className="app-window__metrics">
              {METRICS.map((metric) => (
                <div key={metric.label} className="app-metric">
                  <strong>{metric.value}</strong>
                  <span>{metric.label}</span>
                </div>
              ))}
            </div>

            <div className="app-table">
              <div className="app-table__head">
                <span>Documentos</span>
                <StatusBadge tone="info">{ENVELOPE_STATUS_LABEL.in_progress}</StatusBadge>
              </div>

              <div className="app-table__columns">
                <span>Documento</span>
                <span>Pessoas</span>
                <span>Progresso</span>
                <span>Atualizado</span>
              </div>

              {DOCUMENTS.map((doc) => (
                <div key={doc.name} className="app-table__row">
                  <span className="app-table__doc">{doc.name}</span>
                  <span className="app-table__people">
                    {Array.from({ length: Math.min(doc.people, 3) }).map((_, i) => (
                      <span key={i} className="app-avatar" />
                    ))}
                    {doc.people > 3 ? <span className="app-avatar app-avatar--more" /> : null}
                  </span>
                  <span className="app-table__progress">
                    <span className="app-table__progress-label">
                      {doc.signed}/{doc.total} assinaram
                    </span>
                    <span className="app-bar">
                      <span style={{ width: `${(doc.signed / doc.total) * 100}%` }} />
                    </span>
                  </span>
                  <span className="app-table__when">{doc.when}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="app-window__activity">
            <p className="app-window__activity-title">{OVERVIEW_ATTENTION_TITLE}</p>
            {ATTENTION.map((entry) => (
              <div key={entry.name} className={`app-activity app-activity--${entry.tone}`}>
                <span className="app-activity__text">
                  <strong>{entry.name}</strong>
                  <span>{entry.what}</span>
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>
      </div>
      <figcaption className="app-illustration__caption">Dados ilustrativos</figcaption>
    </figure>
  );
}
